import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { GradientSpinner } from '@/core/components';
import { useAuth } from '@/core/hooks/useAuth';

// アニメーション定義
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RETURN_URL_KEY = 'login_return_url';

/**
 * OAuth コールバックページ
 *
 * Google / Apple からリダイレクトで戻ってきたときに認証を処理し、
 * 元のページにリダイレクトする。
 */
export function AuthCallbackPage() {
  const { user, loading: authLoading, signInWithGoogleCode, signInWithAppleCode } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (processing) return;

    // Google: code がクエリパラメータにある（apple_code がない場合）
    const googleCode = searchParams.get('code');
    const appleCode = searchParams.get('apple_code');

    if (appleCode) {
      // Apple: appleAuthCallback からリダイレクトされてきた
      const state = searchParams.get('state');
      const savedState = sessionStorage.getItem('apple_auth_state');
      const clientId = sessionStorage.getItem('apple_client_id');

      // state 検証（CSRF対策）
      if (state && savedState && state !== savedState) {
        setError('認証に失敗しました（state mismatch）');
        return;
      }

      if (clientId) {
        setProcessing(true);
        sessionStorage.removeItem('apple_auth_state');
        sessionStorage.removeItem('apple_client_id');

        signInWithAppleCode(appleCode, clientId).catch((err) => {
          console.error('Apple login error:', err);
          setError('Appleログインに失敗しました');
        });
      }
      return;
    }

    if (googleCode) {
      setProcessing(true);
      signInWithGoogleCode(googleCode).catch((err) => {
        console.error('Google login error:', err);
        setError('Googleログインに失敗しました');
      });
    }
  }, [searchParams, signInWithGoogleCode, signInWithAppleCode, processing]);

  // ログイン完了 → 元のページへリダイレクト
  if (!authLoading && user) {
    const savedReturnUrl = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    const redirectTo = savedReturnUrl || '/';
    return <Navigate to={redirectTo} replace />;
  }

  // エラー時
  if (error) {
    const savedReturnUrl = sessionStorage.getItem(RETURN_URL_KEY);
    // raceAlias を抽出してログインページに戻る
    const match = savedReturnUrl?.match(/^\/([^/]+)/);
    const raceAlias = match ? match[1] : 'ehime2026';
    return <Navigate to={`/${raceAlias}?error=${encodeURIComponent(error)}`} replace />;
  }

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        <GradientSpinner />
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          ログイン処理中...
        </Typography>
      </Box>
    </Box>
  );
}
